import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation } from '@tanstack/react-query'
import { createShipment, searchPorts, searchAirports } from '@wove/api'
import {
  Card,
  CardHeader,
  CardContent,
  Button,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Label,
  Calendar,
  Popover,
  PopoverContent,
  PopoverTrigger,
  AddressEditor,
} from '@wove/web-ui'
import { ArrowLeft, Save, Loader2, CalendarIcon } from 'lucide-react'
import { toast } from 'sonner'
import { format } from 'date-fns'
import { Address, PortAddress, LocationAddress, isPortAddress } from '@wove/common'
import {
  ShipmentType,
  ShipmentTypeLabels,
  LocationType,
  LocationTypeLabels,
  isOceanFreight,
} from '@wove/common'

const ShipmentCreate: React.FC = () => {
  const navigate = useNavigate()
  const [formData, setFormData] = useState({
    status: 'pending',
    shipmentType: '',
    commodity: '',
    invoiceDate: null as Date | null,
    dueDate: null as Date | null,
    issueDate: null as Date | null,
    shipmentDate: null as Date | null,
    expectedDeliveryDate: null as Date | null,
    readyDate: null as Date | null,
  })

  const [portOfLoading, setPortOfLoading] = useState<Address | null>(null)
  const [portOfDischarge, setPortOfDischarge] = useState<Address | null>(null)
  const [placeOfReceipt, setPlaceOfReceipt] = useState<Address | null>(null)
  const [placeOfDelivery, setPlaceOfDelivery] = useState<Address | null>(null)
  const [placeOfReceiptType, setPlaceOfReceiptType] = useState<LocationType>(LocationType.PORT)
  const [placeOfDeliveryType, setPlaceOfDeliveryType] = useState<LocationType>(LocationType.PORT)

  const createMutation = useMutation({
    mutationFn: createShipment,
    onSuccess: (data) => {
      toast.success('Shipment created successfully')
      navigate(`/shipments/${data.data.id}`)
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || 'Failed to create shipment')
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    // Build the submission data with only non-empty values
    const submitData: any = {}

    // Add basic fields if they have values
    submitData.status = formData.status // Status is always included (defaults to 'pending')
    if (formData.shipmentType) submitData.shipmentType = formData.shipmentType
    if (formData.commodity) submitData.commodity = formData.commodity

    // Add dates if they exist
    if (formData.invoiceDate)
      submitData.invoiceDate = formData.invoiceDate.toISOString().split('T')[0]
    if (formData.dueDate) submitData.dueDate = formData.dueDate.toISOString().split('T')[0]
    if (formData.issueDate) submitData.issueDate = formData.issueDate.toISOString().split('T')[0]
    if (formData.shipmentDate)
      submitData.shipmentDate = formData.shipmentDate.toISOString().split('T')[0]
    if (formData.expectedDeliveryDate)
      submitData.expectedDeliveryDate = formData.expectedDeliveryDate.toISOString().split('T')[0]
    if (formData.readyDate) submitData.readyDate = formData.readyDate.toISOString().split('T')[0]

    // Add port/location codes if they exist
    if (portOfLoading && isPortAddress(portOfLoading)) {
      submitData.portOfLoading = portOfLoading.portCode
    }
    if (portOfDischarge && isPortAddress(portOfDischarge)) {
      submitData.portOfDischarge = portOfDischarge.portCode
    }

    // Handle place of receipt with type
    if (placeOfReceipt) {
      if (placeOfReceipt.type === LocationType.PORT) {
        submitData.placeOfReceiptType = LocationType.PORT
        submitData.placeOfReceiptPort = (placeOfReceipt as PortAddress).portCode
      } else if (placeOfReceipt.type === LocationType.AIRPORT) {
        submitData.placeOfReceiptType = LocationType.AIRPORT
        submitData.placeOfReceiptAirport = (placeOfReceipt as any).airportCode
      } else if (placeOfReceipt.type === LocationType.DOOR) {
        submitData.placeOfReceiptType = LocationType.DOOR
        submitData.placeOfReceiptAddress = placeOfReceipt
      }
    }

    // Handle place of delivery with type
    if (placeOfDelivery) {
      if (placeOfDelivery.type === LocationType.PORT) {
        submitData.placeOfDeliveryType = LocationType.PORT
        submitData.placeOfDeliveryPort = (placeOfDelivery as PortAddress).portCode
      } else if (placeOfDelivery.type === LocationType.AIRPORT) {
        submitData.placeOfDeliveryType = LocationType.AIRPORT
        submitData.placeOfDeliveryAirport = (placeOfDelivery as any).airportCode
      } else if (placeOfDelivery.type === LocationType.DOOR) {
        submitData.placeOfDeliveryType = LocationType.DOOR
        submitData.placeOfDeliveryAddress = placeOfDelivery
      }
    }

    createMutation.mutate(submitData)
  }

  const handleChange = (field: string, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }))
  }

  return (
    <div className="p-4 space-y-4">
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/shipments')}
          leftIcon={<ArrowLeft size={16} />}
        >
          Back
        </Button>
        <h1 className="text-2xl font-bold">Create New Shipment</h1>
      </div>

      <form onSubmit={handleSubmit}>
        <Card>
          <CardHeader>
            <h2 className="text-lg font-semibold">Shipment Details</h2>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Basic Information */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium">Basic Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="status">Status</Label>
                  <Select
                    value={formData.status}
                    onValueChange={(value) => handleChange('status', value)}
                  >
                    <SelectTrigger id="status">
                      <SelectValue placeholder="Select status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="in_progress">In Progress</SelectItem>
                      <SelectItem value="completed">Completed</SelectItem>
                      <SelectItem value="cancelled">Cancelled</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="shipmentType">Shipment Type (Optional)</Label>
                  <Select
                    value={formData.shipmentType}
                    onValueChange={(value) => handleChange('shipmentType', value)}
                  >
                    <SelectTrigger id="shipmentType">
                      <SelectValue placeholder="Select shipment type" />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(ShipmentTypeLabels).map(([value, label]) => (
                        <SelectItem key={value} value={value}>
                          {label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="commodity">Commodity (Optional)</Label>
                  <Input
                    id="commodity"
                    type="text"
                    value={formData.commodity}
                    onChange={(e) => handleChange('commodity', e.target.value)}
                    placeholder="Enter commodity description"
                  />
                </div>
              </div>
            </div>

            {/* Locations */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium">Locations</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {isOceanFreight(formData.shipmentType) && (
                  <>
                    <div className="space-y-2">
                      <Label>Port of Loading (Optional)</Label>
                      <AddressEditor
                        value={
                          portOfLoading ||
                          ({
                            type: LocationType.PORT,
                            portCode: '',
                            portName: '',
                            country: '',
                          } as PortAddress)
                        }
                        onChange={setPortOfLoading}
                        type={LocationType.PORT}
                        searchPorts={searchPorts}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label>Port of Discharge (Optional)</Label>
                      <AddressEditor
                        value={
                          portOfDischarge ||
                          ({
                            type: LocationType.PORT,
                            portCode: '',
                            portName: '',
                            country: '',
                          } as PortAddress)
                        }
                        onChange={setPortOfDischarge}
                        type={LocationType.PORT}
                        searchPorts={searchPorts}
                      />
                    </div>
                  </>
                )}

                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Label>Place of Receipt (Optional)</Label>
                    <Select
                      value={placeOfReceiptType}
                      onValueChange={(value: any) => {
                        setPlaceOfReceiptType(value)
                        setPlaceOfReceipt(null) // Reset when type changes
                      }}
                    >
                      <SelectTrigger className="h-6 w-24 text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(LocationTypeLabels).map(([value, label]) => (
                          <SelectItem key={value} value={value}>
                            {label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {placeOfReceipt && (
                    <AddressEditor
                      value={placeOfReceipt}
                      onChange={setPlaceOfReceipt}
                      type={
                        placeOfReceiptType === LocationType.AIRPORT
                          ? LocationType.AIRPORT
                          : placeOfReceiptType === LocationType.DOOR
                          ? LocationType.DOOR
                          : LocationType.PORT
                      }
                      searchPorts={searchPorts}
                      searchAirports={searchAirports}
                    />
                  )}
                </div>

                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Label>Place of Delivery (Optional)</Label>
                    <Select
                      value={placeOfDeliveryType}
                      onValueChange={(value: any) => {
                        setPlaceOfDeliveryType(value)
                        setPlaceOfDelivery(null) // Reset when type changes
                      }}
                    >
                      <SelectTrigger className="h-6 w-24 text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(LocationTypeLabels).map(([value, label]) => (
                          <SelectItem key={value} value={value}>
                            {label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {placeOfDelivery && (
                    <AddressEditor
                      value={placeOfDelivery}
                      onChange={setPlaceOfDelivery}
                      type={
                        placeOfDeliveryType === LocationType.AIRPORT
                          ? LocationType.AIRPORT
                          : placeOfDeliveryType === LocationType.DOOR
                          ? LocationType.DOOR
                          : LocationType.PORT
                      }
                      searchPorts={searchPorts}
                      searchAirports={searchAirports}
                    />
                  )}
                </div>
              </div>
            </div>

            {/* Dates */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium">Important Dates</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Ready Date (Optional)</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-full justify-start text-left font-normal"
                      >
                        {formData.readyDate ? (
                          format(formData.readyDate, 'PPP')
                        ) : (
                          <span className="text-muted-foreground">Select date</span>
                        )}
                        <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={formData.readyDate || undefined}
                        onSelect={(date) => handleChange('readyDate', date)}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                <div className="space-y-2">
                  <Label>Shipment Date (Optional)</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-full justify-start text-left font-normal"
                      >
                        {formData.shipmentDate ? (
                          format(formData.shipmentDate, 'PPP')
                        ) : (
                          <span className="text-muted-foreground">Select date</span>
                        )}
                        <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={formData.shipmentDate || undefined}
                        onSelect={(date) => handleChange('shipmentDate', date)}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                <div className="space-y-2">
                  <Label>Expected Delivery Date (Optional)</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-full justify-start text-left font-normal"
                      >
                        {formData.expectedDeliveryDate ? (
                          format(formData.expectedDeliveryDate, 'PPP')
                        ) : (
                          <span className="text-muted-foreground">Select date</span>
                        )}
                        <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={formData.expectedDeliveryDate || undefined}
                        onSelect={(date) => handleChange('expectedDeliveryDate', date)}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                <div className="space-y-2">
                  <Label>Invoice Date (Optional)</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-full justify-start text-left font-normal"
                      >
                        {formData.invoiceDate ? (
                          format(formData.invoiceDate, 'PPP')
                        ) : (
                          <span className="text-muted-foreground">Select date</span>
                        )}
                        <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={formData.invoiceDate || undefined}
                        onSelect={(date) => handleChange('invoiceDate', date)}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                <div className="space-y-2">
                  <Label>Due Date (Optional)</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-full justify-start text-left font-normal"
                      >
                        {formData.dueDate ? (
                          format(formData.dueDate, 'PPP')
                        ) : (
                          <span className="text-muted-foreground">Select date</span>
                        )}
                        <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={formData.dueDate || undefined}
                        onSelect={(date) => handleChange('dueDate', date)}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                <div className="space-y-2">
                  <Label>Issue Date (Optional)</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-full justify-start text-left font-normal"
                      >
                        {formData.issueDate ? (
                          format(formData.issueDate, 'PPP')
                        ) : (
                          <span className="text-muted-foreground">Select date</span>
                        )}
                        <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={formData.issueDate || undefined}
                        onSelect={(date) => handleChange('issueDate', date)}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => navigate('/shipments')}>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={createMutation.isPending}
                leftIcon={
                  createMutation.isPending ? (
                    <Loader2 className="animate-spin" size={16} />
                  ) : (
                    <Save size={16} />
                  )
                }
              >
                Create Shipment
              </Button>
            </div>
          </CardContent>
        </Card>
      </form>
    </div>
  )
}

export default ShipmentCreate
